import { Component, OnInit } from '@angular/core';
import { RelatorioDesempenhoService } from 'src/services/relatorio-desempenho.service';
import { EstacaoService } from 'src/services/estacao.service';
import { PessoaService } from 'src/services/pessoa.service';
import * as moment from 'moment';
import * as FileSaver from 'file-saver';

@Component({
    selector: 'app-relatorio-desempenho',
    templateUrl: './relatorio-desempenho.component.html',
    styleUrls: ['./relatorio-desempenho.component.scss'],
    standalone: false
})
export class RelatorioDesempenhoComponent implements OnInit {
  tipoRelatorio: string = 'geral';
  estacoesSelecionadas: any[] = [];
  clientesSelecionados: any[] = [];
  dataInicial: Date;
  dataFinal: Date;
  
  dropDownEstacoes: any[] = [];
  dropDownClientes: any[] = [];
  
  loading: boolean = false;
  loadingPdfDiario: boolean = false;
  exportingPdfDiario: boolean = false;
  relatorioData: any = null;
  relatorioDiarioData: any = null;
  tipoResultado: 'padrao' | 'diario' | null = null;
  
  constructor(
    private relatorioService: RelatorioDesempenhoService,
    private estacaoService: EstacaoService,
    private pessoaService: PessoaService
  ) {
    // Período padrão: último mês, com data final em ontem
    this.dataFinal = moment().subtract(1, 'day').startOf('day').toDate();
    this.dataInicial = moment().subtract(1, 'month').startOf('day').toDate();
  }

  ngOnInit(): void {
    this.carregarEstacoes();
    this.carregarClientes();
  }

  carregarEstacoes(): void {
    this.estacaoService.lookup().subscribe({
      next: (res) => {
        const estacoes = res || [];
        this.dropDownEstacoes = estacoes
          .filter((item: any) => item.ativo !== false)
          .map((item: any) => ({
            label: item.descricao || `Estação ${item.id}`,
            value: item.id
          }));
      },
      error: (err) => {
        console.error('Erro ao carregar estações:', err);
      }
    });
  }

  carregarClientes(): void {
    this.pessoaService.listEmpresas().subscribe({
      next: (res) => {
        const clientes = res.data || res || [];
        this.dropDownClientes = clientes
          .filter((item: any) => item.ativo !== false)
          .map((item: any) => ({
            label: item.nome || `Cliente ${item.id}`,
            value: item.id
          }));
      },
      error: (err) => {
        console.error('Erro ao carregar clientes:', err);
      }
    });
  }

  onTipoRelatorioChange(): void {
    // Limpar seleções quando mudar o tipo
    this.estacoesSelecionadas = [];
    this.clientesSelecionados = [];
  }

  gerarRelatorio(): void {
    if (!this.dataInicial || !this.dataFinal) {
      alert('Por favor, selecione as datas inicial e final.');
      return;
    }

    if (this.dataFinal < this.dataInicial) {
      alert('A data final deve ser maior ou igual à data inicial.');
      return;
    }

    if (this.tipoRelatorio === 'estacoes') {
      if (!this.estacoesSelecionadas || this.estacoesSelecionadas.length === 0) {
        alert('Por favor, selecione pelo menos uma estação.');
        return;
      }
    }

    if (this.tipoRelatorio === 'cliente') {
      if (!this.clientesSelecionados || this.clientesSelecionados.length === 0) {
        alert('Por favor, selecione pelo menos um cliente.');
        return;
      }
    }

    this.loading = true;
    this.relatorioData = null;
    this.relatorioDiarioData = null;
    this.tipoResultado = null;
    const dataInicialStr = moment(this.dataInicial).format('YYYY-MM-DD');
    const dataFinalStr = moment(this.dataFinal).format('YYYY-MM-DD');

    let estacoes: number[] | undefined;
    let clientes: number[] | undefined;
    let geral: boolean | undefined;

    if (this.tipoRelatorio === 'geral') {
      geral = true;
    } else if (this.tipoRelatorio === 'estacoes') {
      estacoes = this.estacoesSelecionadas.map(e => e.value || e);
    } else if (this.tipoRelatorio === 'cliente') {
      clientes = this.clientesSelecionados.map(c => c.value || c);
    }

    this.relatorioService.gerarRelatorio(dataInicialStr, dataFinalStr, estacoes, clientes, geral).subscribe({
      next: (response) => {
        this.relatorioData = this.ordenarEstacoesDoRelatorio(response);
        this.tipoResultado = 'padrao';
        this.loading = false;
      },
      error: (err) => {
        console.error('Erro ao gerar relatório:', err);
        alert('Erro ao gerar relatório. Verifique os dados e tente novamente.');
        this.loading = false;
      }
    });
  }

  exportarXLSX(): void {
    if (!this.relatorioData || !this.relatorioData.data) {
      alert('Nenhum relatório disponível para exportar.');
      return;
    }

    const dadosExport = this.prepararDadosParaExportacao();
    
    import('xlsx').then(xlsx => {
      const worksheet = xlsx.utils.json_to_sheet(dadosExport);
      const workbook = { Sheets: { 'Relatório de Desempenho': worksheet }, SheetNames: ['Relatório de Desempenho'] };
      const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      this.saveAsExcelFile(excelBuffer, 'Relatorio_Desempenho');
    });
  }

  exportarPDF(): void {
    if (!this.relatorioData || !this.relatorioData.data) {
      alert('Nenhum relatório disponível para exportar.');
      return;
    }

    import('jspdf').then(jsPDF => {
      import('jspdf-autotable').then((autoTableModule: any) => {
        const JsPDFCtor = (jsPDF as any).default || jsPDF;
        const autoTable = autoTableModule?.default || autoTableModule?.autoTable;

        if (typeof autoTable !== 'function') {
          throw new Error('Não foi possível carregar o módulo de exportação PDF.');
        }

        const doc = new JsPDFCtor('landscape');
        const marginLeft = 14;

        doc.setFontSize(16);
        doc.text('Relatório de Desempenho', marginLeft, 15);

        doc.setFontSize(10);
        const periodo = `Período: ${moment(this.dataInicial).format('DD/MM/YYYY')} a ${moment(this.dataFinal).format('DD/MM/YYYY')}`;
        doc.text(periodo, marginLeft, 22);

        let startY = 30;

        if (this.tipoRelatorio === 'cliente' && this.relatorioData.data) {
          this.relatorioData.data.forEach((cliente: any) => {
            if (startY > 180) {
              doc.addPage();
              startY = 20;
            }

            doc.setFontSize(12);
            doc.text(`Cliente: ${cliente.cliente_nome}`, marginLeft, startY);
            startY += 8;

            doc.setFontSize(10);
            doc.text(
              `Média Coleta: ${cliente.media_coleta?.toFixed(2)}% | Média Integração: ${cliente.media_integracao?.toFixed(2)}% | Total Estações: ${cliente.total_estacoes}`,
              marginLeft,
              startY
            );
            startY += 10;

            if (cliente.estacoes && cliente.estacoes.length > 0) {
              autoTable(doc, {
                startY,
                head: [['Estação', 'Coleta (%)', 'Integração (%)', 'Intervalo (min)', 'Dias em Monitoramento']],
                body: cliente.estacoes.map((est: any) => [
                  est.estacao_nome || '-',
                  est.indice_coleta?.toFixed(2) || '0.00',
                  est.indice_integracao_medio?.toFixed(2) || '0.00',
                  est.intervalo_operacao ?? '-',
                  String(est.dias_em_monitoramento || 0)
                ]),
                margin: { left: marginLeft, right: marginLeft },
                styles: { fontSize: 9 }
              });

              startY = (doc as any).lastAutoTable.finalY + 10;
            }
          });
        } else if (this.relatorioData.data) {
          autoTable(doc, {
            startY,
            head: [['Estação', 'Cliente', 'Coleta (%)', 'Integração (%)', 'Intervalo (min)', 'Dias em Monitoramento']],
            body: this.relatorioData.data.map((est: any) => [
              est.estacao_nome || '-',
              est.cliente_nome || '-',
              est.indice_coleta?.toFixed(2) || '0.00',
              est.indice_integracao_medio?.toFixed(2) || '0.00',
              est.intervalo_operacao ?? '-',
              String(est.dias_em_monitoramento || 0)
            ]),
            margin: { left: marginLeft, right: marginLeft },
            styles: { fontSize: 9 }
          });

          startY = (doc as any).lastAutoTable.finalY + 10;
        }

        if (this.relatorioData.resumo) {
          if (startY > 180) {
            doc.addPage();
            startY = 20;
          }

          doc.setFontSize(12);
          doc.text('Resumo Geral', marginLeft, startY);
          startY += 8;

          doc.setFontSize(10);
          doc.text(`Total de Estações: ${this.relatorioData.resumo.total_estacoes}`, marginLeft, startY);
          startY += 6;
          doc.text(`Média Geral de Coleta: ${this.relatorioData.resumo.media_coleta_geral?.toFixed(2)}%`, marginLeft, startY);
          startY += 6;
          doc.text(`Média Geral de Integração: ${this.relatorioData.resumo.media_integracao_geral?.toFixed(2)}%`, marginLeft, startY);
        }

        doc.save(`Relatorio_Desempenho_${moment().format('YYYY-MM-DD_HH-mm-ss')}.pdf`);
      }).catch((err) => {
        console.error('Erro ao exportar PDF:', err);
        alert('Erro ao exportar PDF. Tente novamente.');
      });
    }).catch((err) => {
      console.error('Erro ao carregar biblioteca PDF:', err);
      alert('Erro ao carregar biblioteca de PDF. Tente novamente.');
    });
  }

  prepararDadosParaExportacao(): any[] {
    const dados: any[] = [];
    
    if (this.tipoRelatorio === 'cliente' && this.relatorioData.data) {
      this.relatorioData.data.forEach((cliente: any) => {
        if (cliente.estacoes && cliente.estacoes.length > 0) {
          cliente.estacoes.forEach((est: any) => {
            dados.push({
              'Cliente': cliente.cliente_nome,
              'Estação': est.estacao_nome,
              'Índice de Coleta (%)': est.indice_coleta?.toFixed(2) || '0.00',
              'Índice de Integração (%)': est.indice_integracao_medio?.toFixed(2) || '0.00',
              'Intervalo Operação (min)': est.intervalo_operacao || '-',
              'Dias em Monitoramento': est.dias_em_monitoramento || 0
            });
          });
        } else {
          dados.push({
            'Cliente': cliente.cliente_nome,
            'Estação': '-',
            'Índice de Coleta (%)': cliente.media_coleta?.toFixed(2) || '0.00',
            'Índice de Integração (%)': cliente.media_integracao?.toFixed(2) || '0.00',
            'Intervalo Operação (min)': '-',
            'Dias em Monitoramento': 0
          });
        }
      });
    } else if (this.relatorioData.data) {
      this.relatorioData.data.forEach((est: any) => {
        dados.push({
          'Estação': est.estacao_nome,
          'Cliente': est.cliente_nome || '-',
          'Índice de Coleta (%)': est.indice_coleta?.toFixed(2) || '0.00',
          'Índice de Integração (%)': est.indice_integracao_medio?.toFixed(2) || '0.00',
          'Intervalo Operação (min)': est.intervalo_operacao || '-',
          'Dias em Monitoramento': est.dias_em_monitoramento || 0
        });
      });
    }
    
    return dados;
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    FileSaver.saveAs(data, fileName + '_' + moment().format('YYYY-MM-DD_HH-mm-ss') + EXCEL_EXTENSION);
  }

  private montarFiltrosRelatorio(): { dataInicialStr: string; dataFinalStr: string; estacoes?: number[]; clientes?: number[]; geral?: boolean } | null {
    if (!this.dataInicial || !this.dataFinal) {
      alert('Por favor, selecione as datas inicial e final.');
      return null;
    }

    if (this.dataFinal < this.dataInicial) {
      alert('A data final deve ser maior ou igual à data inicial.');
      return null;
    }

    if (this.tipoRelatorio === 'estacoes') {
      if (!this.estacoesSelecionadas || this.estacoesSelecionadas.length === 0) {
        alert('Por favor, selecione pelo menos uma estação.');
        return null;
      }
    }

    if (this.tipoRelatorio === 'cliente') {
      if (!this.clientesSelecionados || this.clientesSelecionados.length === 0) {
        alert('Por favor, selecione pelo menos um cliente.');
        return null;
      }
    }

    const dataInicialStr = moment(this.dataInicial).format('YYYY-MM-DD');
    const dataFinalStr = moment(this.dataFinal).format('YYYY-MM-DD');

    let estacoes: number[] | undefined;
    let clientes: number[] | undefined;
    let geral: boolean | undefined;

    if (this.tipoRelatorio === 'geral') {
      geral = true;
    } else if (this.tipoRelatorio === 'estacoes') {
      estacoes = this.estacoesSelecionadas.map(e => e.value || e);
    } else if (this.tipoRelatorio === 'cliente') {
      clientes = this.clientesSelecionados.map(c => c.value || c);
    }

    return { dataInicialStr, dataFinalStr, estacoes, clientes, geral };
  }

  exportarPDFDiario(): void {
    const filtros = this.montarFiltrosRelatorio();
    if (!filtros) {
      return;
    }

    const diasPeriodo = moment(filtros.dataFinalStr).diff(moment(filtros.dataInicialStr), 'days') + 1;
    if (diasPeriodo > 32) {
      alert('O PDF Diário permite no máximo 32 dias. Ajuste o período e tente novamente.');
      return;
    }

    this.loadingPdfDiario = true;
    this.relatorioData = null;
    this.relatorioDiarioData = null;
    this.tipoResultado = null;

    this.relatorioService.gerarRelatorioDiario(
      filtros.dataInicialStr,
      filtros.dataFinalStr,
      filtros.estacoes,
      filtros.clientes,
      filtros.geral
    ).subscribe({
      next: (response) => {
        this.relatorioDiarioData = this.ordenarRelatorioDiario(response);
        this.tipoResultado = 'diario';
        this.loadingPdfDiario = false;
      },
      error: (err) => {
        console.error('Erro ao buscar dados do PDF diário:', err);
        const msg = err?.error?.errors?.periodo?.[0]
          || err?.error?.message
          || 'Erro ao gerar PDF diário. Verifique o período (máx. 32 dias) e tente novamente.';
        alert(msg);
        this.loadingPdfDiario = false;
      }
    });
  }

  async exportarRelatorioPDFDiario(): Promise<void> {
    if (!this.relatorioDiarioData) {
      return;
    }

    this.exportingPdfDiario = true;
    try {
      await this.gerarPdfDiarioDocumento(this.relatorioDiarioData);
    } catch (err) {
      console.error('Erro ao gerar PDF diário:', err);
      alert('Erro ao gerar PDF diário. Tente novamente.');
    } finally {
      this.exportingPdfDiario = false;
    }
  }

  formatarDia(dia: string): string {
    return moment(dia).format('DD/MM');
  }

  corFundoIntegracaoParaTela(indiceIntegracao: number): string {
    if (indiceIntegracao > 90) {
      return '#c8e6c9';
    }
    if (indiceIntegracao >= 80) {
      return '#fff9c4';
    }
    return '#ffcdd2';
  }

  private ordenarRelatorioDiario(relatorio: any): any {
    return {
      ...relatorio,
      clientes: (relatorio?.clientes || []).map((cliente: any) => ({
        ...cliente,
        estacoes: this.ordenarEstacoes(cliente.estacoes)
      }))
    };
  }

  private async gerarPdfDiarioDocumento(dados: any): Promise<void> {
    const jsPDFModule = await import('jspdf');
    const autoTableModule: any = await import('jspdf-autotable');
    const JsPDFCtor = (jsPDFModule as any).jsPDF || (jsPDFModule as any).default || jsPDFModule;
    const autoTable = autoTableModule?.default || autoTableModule?.autoTable;

    if (typeof autoTable !== 'function') {
      throw new Error('Não foi possível carregar o módulo de exportação PDF.');
    }

    const clientes = (dados?.clientes || []).map((cliente: any) => ({
      ...cliente,
      estacoes: this.ordenarEstacoes(cliente.estacoes)
    }));
    const dias: string[] = dados?.dias || [];

    if (!clientes.length || !dias.length) {
      alert('Nenhum dado encontrado para o período selecionado.');
      return;
    }

    const doc = new JsPDFCtor({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const margin = 4;
    const pageWidth = doc.internal.pageSize.getWidth();
    const usableWidth = pageWidth - margin * 2;
    const estacaoColWidth = 24;
    const mediaColWidth = 14;
    const dayColWidth = Math.max(6, (usableWidth - estacaoColWidth - mediaColWidth) / dias.length);
    const mediaColIndex = dias.length + 1;
    const headerTop = 20;

    const headDias = dias.map((d: string) => moment(d).format('DD/MM'));
    const head = [['Estação', ...headDias, 'Média']];
    const periodoLabel = `Período: ${moment(dados.periodo?.data_inicial || this.dataInicial).format('DD/MM/YYYY')} a ${moment(dados.periodo?.data_final || this.dataFinal).format('DD/MM/YYYY')}`;
    let ultimoResumoBottomY = headerTop;

    clientes.forEach((cliente: any, clienteIndex: number) => {
      if (clienteIndex > 0) {
        doc.addPage();
      }

      const desenharCabecalhoCliente = () => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(cliente.cliente_nome || 'Cliente', margin, 9);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.text(periodoLabel, margin, 13.5);
        doc.text(
          `Média Coleta: ${Math.round(cliente.media_coleta || 0)}%  |  Média Integração: ${Math.round(cliente.media_integracao || 0)}%  |  Estações: ${cliente.total_estacoes || 0}`,
          margin,
          17
        );
      };

      desenharCabecalhoCliente();

      const body = (cliente.estacoes || []).map((est: any) => {
        const row: any[] = [est.estacao_nome || '-'];
        let somaColeta = 0;
        let somaIntegracao = 0;

        dias.forEach((dia: string) => {
          const cell = est.dias?.[dia] || { indice_coleta: 0, indice_integracao: 0 };
          const coletaBruta = Number(cell.indice_coleta) || 0;
          const integracaoBruta = Number(cell.indice_integracao) || 0;
          somaColeta += coletaBruta;
          somaIntegracao += integracaoBruta;

          const coleta = Math.round(coletaBruta);
          const integracao = Math.round(integracaoBruta);
          row.push({
            content: `${coleta}\n${integracao}`,
            styles: {
              fillColor: this.corFundoIntegracao(integracao),
              halign: 'center',
              valign: 'middle',
            },
          });
        });

        const qtdDias = dias.length || 1;
        row.push({
          content: `${Math.round(somaColeta / qtdDias)}\n${Math.round(somaIntegracao / qtdDias)}`,
          styles: {
            fillColor: [214, 232, 250],
            textColor: [13, 71, 161],
            fontStyle: 'bold',
            halign: 'center',
            valign: 'middle',
            lineColor: [25, 90, 170],
            lineWidth: 0.45,
          },
        });
        return row;
      });

      autoTable(doc, {
        startY: headerTop,
        head,
        body,
        theme: 'grid',
        margin: { left: margin, right: margin, top: headerTop, bottom: margin },
        styles: {
          fontSize: 5,
          cellPadding: 0.3,
          overflow: 'linebreak',
          lineColor: [180, 180, 180],
          lineWidth: 0.1,
          textColor: [40, 40, 40],
        },
        headStyles: {
          fillColor: [70, 70, 70],
          textColor: [255, 255, 255],
          fontSize: 4.2,
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
          cellPadding: 0.4,
          overflow: 'hidden',
        },
        columnStyles: {
          0: {
            cellWidth: estacaoColWidth,
            fontSize: 5,
            halign: 'left',
            valign: 'middle',
            overflow: 'linebreak',
            cellPadding: 0.5,
          },
          ...Object.fromEntries(
            dias.map((_, i) => [i + 1, { cellWidth: dayColWidth, halign: 'center', valign: 'middle' }])
          ),
          [mediaColIndex]: {
            cellWidth: mediaColWidth,
            halign: 'center',
            valign: 'middle',
            fontStyle: 'bold',
          },
        },
        showHead: 'everyPage',
        didDrawPage: (data: any) => {
          if (data.pageNumber > 1 || clienteIndex > 0) {
            // Em páginas adicionais do mesmo cliente, redesenha o cabeçalho
            if (data.pageNumber > 1) {
              desenharCabecalhoCliente();
            }
          }
        },
        didParseCell: (data: any) => {
          if (data.section === 'head' && data.column.index === mediaColIndex) {
            data.cell.styles.fillColor = [25, 90, 170];
            data.cell.styles.textColor = [255, 255, 255];
            data.cell.styles.fontStyle = 'bold';
          }
          if (data.section === 'body' && data.column.index > 0) {
            const raw = data.cell.raw;
            if (raw && typeof raw === 'object' && raw.styles) {
              if (raw.styles.fillColor) {
                data.cell.styles.fillColor = raw.styles.fillColor;
              }
              if (raw.styles.textColor) {
                data.cell.styles.textColor = raw.styles.textColor;
              }
              if (raw.styles.fontStyle) {
                data.cell.styles.fontStyle = raw.styles.fontStyle;
              }
              if (raw.styles.lineColor) {
                data.cell.styles.lineColor = raw.styles.lineColor;
              }
              if (raw.styles.lineWidth) {
                data.cell.styles.lineWidth = raw.styles.lineWidth;
              }
            }
          }
        },
      });

      const finalY = (doc as any).lastAutoTable?.finalY ?? headerTop;
      let resumoY = finalY + 5;
      const pageHeight = doc.internal.pageSize.getHeight();
      if (resumoY > pageHeight - 12) {
        doc.addPage();
        desenharCabecalhoCliente();
        resumoY = 24;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.text('Resumo do Cliente', margin, resumoY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.text(
        `Total de Estações: ${cliente.total_estacoes || 0}  |  Média Coleta: ${Math.round(cliente.media_coleta || 0)}%  |  Média Integração: ${Math.round(cliente.media_integracao || 0)}%`,
        margin,
        resumoY + 4
      );
      ultimoResumoBottomY = resumoY + 4;
    });

    const resumo = dados?.resumo || {};
    const totalEstacoes = resumo.total_estacoes
      ?? clientes.reduce((acc: number, c: any) => acc + (c.total_estacoes || 0), 0);
    const mediaColetaGeral = resumo.media_coleta_geral != null
      ? Math.round(Number(resumo.media_coleta_geral) || 0)
      : (totalEstacoes > 0
        ? Math.round(clientes.reduce((acc: number, c: any) => acc + (c.media_coleta || 0) * (c.total_estacoes || 0), 0) / totalEstacoes)
        : 0);
    const mediaIntegracaoGeral = resumo.media_integracao_geral != null
      ? Math.round(Number(resumo.media_integracao_geral) || 0)
      : (totalEstacoes > 0
        ? Math.round(clientes.reduce((acc: number, c: any) => acc + (c.media_integracao || 0) * (c.total_estacoes || 0), 0) / totalEstacoes)
        : 0);

    const pageHeight = doc.internal.pageSize.getHeight();
    let totalY = ultimoResumoBottomY + 6;
    if (totalY > pageHeight - 12) {
      doc.addPage();
      totalY = 16;
    }

    doc.setDrawColor(120, 120, 120);
    doc.setLineWidth(0.3);
    doc.line(margin, totalY, pageWidth - margin, totalY);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.text(
      `Total de Estações: ${totalEstacoes}  |  Média Coleta: ${mediaColetaGeral}%  |  Média Integração: ${mediaIntegracaoGeral}%`,
      margin,
      totalY + 5
    );

    doc.save(`Relatorio_Desempenho_Diario_${moment().format('YYYY-MM-DD_HH-mm-ss')}.pdf`);
  }

  private corFundoIntegracao(indiceIntegracao: number): [number, number, number] {
    if (indiceIntegracao > 90) {
      return [200, 230, 201]; // verde clarinho
    }
    if (indiceIntegracao >= 80) {
      return [255, 249, 196]; // amarelo clarinho
    }
    return [255, 205, 210]; // vermelho clarinho
  }

  private ordenarEstacoesDoRelatorio(relatorio: any): any {
    if (!relatorio) {
      return relatorio;
    }

    if (this.tipoRelatorio === 'cliente') {
      return {
        ...relatorio,
        data: (relatorio.data || []).map((cliente: any) => ({
          ...cliente,
          estacoes: this.ordenarEstacoes(cliente.estacoes)
        }))
      };
    }

    return {
      ...relatorio,
      data: this.ordenarEstacoes(relatorio.data)
    };
  }

  private ordenarEstacoes(estacoes: any[] | undefined): any[] {
    return [...(estacoes || [])].sort((a: any, b: any) =>
      (a?.estacao_nome || '').localeCompare(
        b?.estacao_nome || '',
        'pt-BR',
        { sensitivity: 'base' }
      )
    );
  }
}

